import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useModal } from "../../../hooks/useModal";
import { Button } from "../../../components/core/Button";
import { Input } from "../../../components/core/Input";
import { InputEmail } from "../../../components/others/InputEmail";
import LavorisService from "../../../services/LavorisService";
import { PrimaryButton } from "../../../components/others/ButtonPrimary";
import { ModalCheckEmail } from "../modalCheckEmail";
import { DatePicker } from "../../../components/others/DatePicker";
import { dateToString } from "../../../utils/dateToString";
import SelectProvincias from "../../../components/others/SelectProvincias/SelectProvincias";
import { PhoneNumber } from "../../../components/others/PhoneNumber";
import { sexs, maritalStatuses } from "../../../utils/dataOptionsUsers";
import Select from "../../../components/others/Select/Select";
import InputCuil from "../../../components/others/InputCuil/InputCuil";
import CuilRadioInput from "../../../components/others/CuilRadioInput/CuilRadioInput";
import Textarea from "../../../components/others/Textarea/Textarea";
import moment from "moment";
import { useAlert } from "../../../hooks/useAlert";

const EMAIL_CHECKER = "emailChecker"

type FormPreValues = {
  email: string;
  password: string;
  repeatPassword: string;
  name: string;
  lastname: string;
  DNI: string;
  birthDate: Date;
  cuil: string;
  residence: string;
  location: string;
  province: string;
  phoneNumber: string;
  postalCode: string;
  sex: string;
  genero: string;
  maritalStatus?: string;
  job?: string;
  comment?: string;
};

export default function AddParticularPatient() {
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<FormPreValues>({ mode: "all" });

  const adminMail = watch("email") || "";

  const { openModal, isActive } = useModal();
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [registerError, setRegisterError] = useState(false);
  const [province, setProvince] = useState("");
  const [hasManagedBudget, setHasManagedBudget] = useState(false);

  const cuil = watch("cuil") || " ";
  const [hasCuil, setHasCuil] = useState(true);
  const [dni, setDni] = useState("");
  const dniValue = cuil.replace(/[^0-9]/g, "").substring(2, 10);

  useEffect(() => {
    setDni(dniValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuil]);

  const LavorisInstance = LavorisService.getInstance();
  let navigate = useNavigate();

  useEffect(() => {
    reset();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = (selectedValue: any) => {
    setProvince(selectedValue);
  };

  const onSubmit = async (data: FormPreValues) => {
    if (!hasManagedBudget) return;
    setIsLoading(true);
    setRegisterError(false);
    const mappedData = {
      ...data,
      name: `${data.lastname}, ${data.name}`,
      birthDate: dateToString(data.birthDate),
      residence: " ",
      province: province,
      supplementaryReference: "",
      cuil:
        data.cuil === undefined || data.cuil === "" ? "Pendiente" : data.cuil,
      DNI: data.DNI.trim().length === 0 ? dni : data.DNI,
      particularStartDate: moment().format("DD/MM/YYYY"),
    };

    await LavorisInstance.ParticularPatient.addParticularPatient(mappedData)
      .then(() => {
        openModal(EMAIL_CHECKER);
      })
      .catch((err) => {
        console.log(err);
        let message = err.response.data.message;
        showAlert({title: 'Ha ocurrido un error', subtitle: message});
        // alert(message)
        //setRegisterError(true);
      })
      .finally(() => setIsLoading(false));
  };

  const onCancel = () => {
    navigate("/");
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid w-full gap-6 grid-flow rows"
      >
        {/* Datos Personales - Inicio */}
        <h6 className="subtitle">
          Información personal
        </h6>
        <Input
          type="text"
          label="Apellido"
          fieldName="lastname"
          register={register}
          required
          error={errors?.lastname}
        />
        <Input
          type="text"
          label="Nombre"
          fieldName="name"
          register={register}
          required
          error={errors?.name}
        />
        <Input
          type="text"
          label="Ocupación"
          fieldName="job"
          register={register}
          required
          error={errors?.DNI}
        />

        <CuilRadioInput hasCuil={hasCuil} setHasCuil={setHasCuil} />
        {hasCuil ? (
          <>
            <InputCuil
              label="CUIL (Sin espacios ni guiones)"
              fieldName="cuil"
              register={register}
              error={errors?.cuil}
              required={hasCuil}
              readOnly={!hasCuil}
            />
            <Input
              type="text"
              label="DNI"
              fieldName="DNI"
              register={register}
              error={errors?.DNI}
              readOnly={true}
              value={dni}
            />
          </>
        ) : (
          <Input
            type="text"
            label="DNI o Pasaporte"
            fieldName="DNI"
            register={register}
            error={errors?.DNI}
            required
            readOnly={false}
          />
        )}
        <DatePicker
          fieldName="birthDate"
          label={"Fecha de Nacimiento"}
          minDate={100}
          maxDate={18}
          control={control}
          required
          error={errors?.birthDate}
        />
        <Select
          fieldName="maritalStatus"
          label="Estado Civil"
          options={maritalStatuses}
          register={register}
          error={errors?.maritalStatus}
          required
          styles="rounded w-full h-[50px]"
        />
        <Select
          fieldName="sex"
          label="Sexo biológico"
          options={sexs}
          register={register}
          error={errors?.sex}
          required
          styles="rounded w-full h-[50px]"
        />
        <Input
          type="text"
          label="Género"
          fieldName="genre"
          register={register}
          error={errors?.genero}
        />

        <h6 className="subtitle">
          Información de contacto
        </h6>
        <SelectProvincias onSelect={handleSelect} defaultValue={province} />
        <Input
          type="text"
          label="Localidad o Barrio"
          fieldName="location"
          register={register}
          required
          error={errors?.location}
        />

        <Input
          type="text"
          label="Dirección"
          fieldName="fullAddress"
          register={register}
          required
          error={errors?.residence}
        />

        <Input
          type="text"
          label="Código postal"
          fieldName="postalCode"
          register={register}
          required
          error={errors?.postalCode}
        />

        <PhoneNumber
          label="Teléfono"
          fieldName="phoneNumber"
          control={control}
          error={errors?.phoneNumber}
          required
        />

        <h6 className="subtitle">Creación de cuenta</h6>
        <div className="w-auto bg-[#F5F5F5] p-1 border-[md] ">
          <small>
            *Se enviará un mail a esta dirección para comenzar con el proceso
            de alta
          </small>
        </div>
        <InputEmail
          label="E-mail"
          nameInput="email"
          register={register}
          required
          error={errors}
        />
        <Textarea
          register={register}
          fieldName="comment"
          label="Comentarios"
          error={errors.comment}
          rows={4}
          styles="rounded w-full"
        />
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hasManagedBudget}
            onChange={(e) => setHasManagedBudget(e.target.checked)}
            className="rounded text-red-500 focus:ring-red-400"
          />
          <span className="text-red-500">
            Ya cuento con un presupuesto gestionado en el Cel. 3513700999
          </span>
        </label>
        <div className="flex justify-center">
          <div className="w-100">
            {/* <Button
              type="button"
              text="Cancelar"
              className="bg-white font-bold text-red-500 border-red-500"
              onClick={onCancel}
            /> */}
            <PrimaryButton
              type={hasManagedBudget ? "submit" : "button"}
              text="Crear cuenta"
              styles={`font-bold text-[#FFFFFF] rounded-md h-12 w-100 py-2 ${
                hasManagedBudget
                  ? "border-[#fb554f] bg-[#fb544f]"
                  : "border-gray-400 bg-gray-400 cursor-not-allowed"
              }`}
              disabled={isLoading}
            />
          </div>
        </div>
        {registerError && (
          <small className="flex w-full mt-2 justify-center text-center text-red-500">
            {`No se pudo crear el usuario, intente nuevamente más tarde
            Si sigue asi consulte con Lavoris.`}
          </small>
        )}
      </form>
      {isActive(EMAIL_CHECKER) && (
        <ModalCheckEmail urlTittle={EMAIL_CHECKER} mail={adminMail} show />
      )}
    </>
  );
}
